/* eslint-disable consistent-return */
/* eslint-disable no-console */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { CLOUD_FLARE_CAPTCHA, GOOGLE_RECAPTCHA, NONE_CAPTCHA } from '../constants/network';
import FormularioService from '../API/FormularioService';

export const useCloudflareScript = ({ elementId, options }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [loaded, setLoaded] = useState(false);
  const [token, setToken] = useState(null);
  const [captchaType, setCaptchaType] = useState(null);

  const widgetIdRef = useRef(null);

  const renderCloudflareWidget = useCallback(() => {
    const element = document.getElementById(elementId);

    if (!element) {
      console.error(`Element with ID ${elementId} not found.`);
      return null;
    }

    widgetIdRef.current = window.turnstile.render(element, {
      sitekey: process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY,
      callback: token => {
        setToken(token);
        setLoaded(true);
      },
      appearance: 'interaction-only',
      ...options,
    });
  }, [elementId, options]);

  const loadScript = useCallback(() => {
    const existingScript = document.querySelector(
      'script[src^="https://challenges.cloudflare.com/turnstile/v0/api.js"]'
    );

    if (existingScript) {
      renderCloudflareWidget();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';

    script.onload = () => {
      renderCloudflareWidget();
    };

    document.body.appendChild(script);
  }, [renderCloudflareWidget]);

  const loadGoogleRecaptcha = useCallback(async () => {
    if (!executeRecaptcha) {
      console.error('executeRecaptcha no está disponible.');
      return;
    }

    setLoaded(true);
  }, [executeRecaptcha]);

  const getToken = useCallback(
    async key => {
      if (captchaType === CLOUD_FLARE_CAPTCHA) {
        if (!token && widgetIdRef.current !== null) {
          await window.turnstile.getResponse(widgetIdRef.current);
        }

        if (token) {
          return token;
        }
        console.error('Token de Cloudflare no disponible.');
      }

      if (captchaType === GOOGLE_RECAPTCHA) {
        const respToken = await executeRecaptcha(key);
        return respToken;
      }
    },
    [token, captchaType, executeRecaptcha]
  );

  const deleteRecaptchaWidget = useCallback(() => {
    setTimeout(() => {
      const el = document.querySelector('.grecaptcha-badge');
      if (el) {
        el.style.display = 'none';
      }
    }, 500);
  }, []);

  useEffect(() => {
    const getCaptchaStatus = async () => {
      try {
        const resp = await FormularioService.getTypeCaptcha();

        if (resp === CLOUD_FLARE_CAPTCHA) {
          loadScript();
          setCaptchaType(CLOUD_FLARE_CAPTCHA);
          deleteRecaptchaWidget();
        } else if (resp === GOOGLE_RECAPTCHA) {
          setCaptchaType(GOOGLE_RECAPTCHA);
          loadGoogleRecaptcha();
        } else if (resp === NONE_CAPTCHA) {
          setCaptchaType(NONE_CAPTCHA);
          setLoaded(true);
          deleteRecaptchaWidget();
        }
      } catch (error) {
        console.error('Error fetching captcha type:', error);
      }
    };

    getCaptchaStatus();
  }, []);

  useEffect(() => {
    if (captchaType === GOOGLE_RECAPTCHA) {
      loadGoogleRecaptcha();
    }
  }, [captchaType, executeRecaptcha, loadGoogleRecaptcha]);

  return { loaded, captchaType, getToken };
};
